import { useState } from "react";
import { Search, Plus, Trash2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

const UnitCodeMapper = () => {
  const { toast } = useToast();

  const commonUnits = [
    { Code: "PCE", Name: "Piece" },
    { Code: "KGM", Name: "kilogram" },
    { Code: "MTR", Name: "metre" },
    { Code: "LTR", Name: "litre" },
    { Code: "MTQ", Name: "cubic metre" },
    { Code: "TNE", Name: "tonne (metric ton)" },
    { Code: "MTK", Name: "square metre" },
    { Code: "EA", Name: "each" },
    { Code: "KWH", Name: "kilowatt hour" },
    { Code: "PR", Name: "pair" },
    { Code: "LBR", Name: "pound" },
    { Code: "GRM", Name: "gram" },
    { Code: "CMT", Name: "centimetre" },
    { Code: "DZN", Name: "dozen" },
    { Code: "MLT", Name: "millilitre" },
    { Code: "MMT", Name: "millimetre" },
    { Code: "HUR", Name: "hour" },
    { Code: "MON", Name: "month" },
    { Code: "DAY", Name: "day" },
    { Code: "WEE", Name: "week" },
    { Code: "SET", Name: "set" },
    { Code: "ROL", Name: "roll" },
    { Code: "KTM", Name: "kilometre" },
    { Code: "CMK", Name: "square centimetre" },
    { Code: "GLL", Name: "gallon (US)" },
    { Code: "FTK", Name: "square foot" },
    { Code: "FTQ", Name: "cubic foot" },
    { Code: "INH", Name: "inch" },
    { Code: "FOT", Name: "foot" },
    { Code: "YRD", Name: "yard" },
    { Code: "MIL", Name: "thousand" },
    { Code: "KMH", Name: "kilometre per hour" },
    { Code: "CEL", Name: "degree Celsius" },
    { Code: "FAH", Name: "degree Fahrenheit" },
    { Code: "ANN", Name: "year" },
    { Code: "HLT", Name: "hectolitre" },
    { Code: "ONZ", Name: "ounce (avoirdupois)" },
    { Code: "PTL", Name: "liquid pint (US)" },
    { Code: "QTL", Name: "liquid quart (US)" },
    { Code: "GLD", Name: "dry gallon (US)" },
    { Code: "MGM", Name: "milligram" },
    { Code: "KMQ", Name: "kilogram per cubic metre" },
    { Code: "NPT", Name: "number of parts" },
    { Code: "NRL", Name: "number of rolls" },
    { Code: "PTN", Name: "portion" },
    { Code: "BTL", Name: "bottle" },
    { Code: "BX", Name: "box" },
    { Code: "CAN", Name: "can" },
    { Code: "CTN", Name: "carton" },
    { Code: "PAL", Name: "pascal" },
  ];

  const [mappings, setMappings] = useState<
    { unitCode: string; unitName: string; customKey: string }[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [customKey, setCustomKey] = useState("");
  const [selectedCode, setSelectedCode] = useState<{
    Code: string;
    Name: string;
  } | null>(null);

  const availableCodes = commonUnits.filter(
    (unit) =>
      !mappings.some((mapping) => mapping.unitCode === unit.Code) &&
      (unit.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        unit.Code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleAddMapping = () => {
    if (!selectedCode || !customKey.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a unit and enter a custom key",
      });
      return;
    }

    if (mappings.some((m) => m.customKey === customKey.trim())) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Custom key already exists",
      });
      return;
    }

    setMappings([
      ...mappings,
      {
        unitCode: selectedCode.Code,
        unitName: selectedCode.Name,
        customKey: customKey.trim(),
      },
    ]);

    setCustomKey("");
    setSelectedCode(null);
    toast({
      title: "Success",
      description: "Mapping added successfully",
    });
  };

  const handleRemoveMapping = (customKey: string) => {
    setMappings(mappings.filter((m) => m.customKey !== customKey));
    toast({
      title: "Success",
      description: "Mapping removed successfully",
    });
  };

  return (
    <div className="mx-36">
      <Card className="mb-6 border-none">
        <CardHeader>
          <CardTitle>Unit Code Mapper</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search units..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 shadow"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4 shadow">
                <h3 className="font-medium mb-2">Available Units</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {availableCodes.map((unit) => (
                    <div
                      key={unit.Code}
                      onClick={() => setSelectedCode(unit)}
                      onKeyUp={(e) =>
                        e.key === "Enter" && setSelectedCode(unit)
                      }
                      onKeyDown={(e) => e.key === " " && setSelectedCode(unit)}
                      className={`p-2 rounded cursor-pointer ${
                        selectedCode?.Code === unit.Code
                          ? "bg-blue-100 border border-blue-300"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <div className="font-medium">{unit.Name}</div>
                      <div className="text-sm text-gray-600">
                        Code: {unit.Code}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border rounded-lg shadow p-4">
                <h3 className="font-medium mb-2">Mapped Units</h3>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter custom key"
                      value={customKey}
                      onChange={(e) => setCustomKey(e.target.value)}
                    />
                    <Button onClick={handleAddMapping}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {mappings.map((mapping) => (
                      <div
                        key={mapping.customKey}
                        className="flex items-center justify-between p-2 border rounded"
                      >
                        <div>
                          <div className="font-medium">{mapping.customKey}</div>
                          <div className="text-sm text-gray-600">
                            {mapping.unitName} ({mapping.unitCode})
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMapping(mapping.customKey)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnitCodeMapper;
